import { shouldDispose } from "./dispose.ts";
import { getDebug } from "./utils.ts";

export type GetEvent = {
    action : string;
    proxy: any;
    prop: string | symbol;
}

export type GetHook = (event: GetEvent) => any;

export type SubscribeEvent<Target, Value> = {
    action : string;
    target : Target;
    prop : string | symbol;
    value? : Value;
    oldValue : Value;
}

export type SubscribeCallback<Target, Value> = (event: SubscribeEvent<Target, Value>) => any;

const targetSymbol = Symbol("target");
let nextTargetId = 1;

let getHook : GetHook | undefined;

/** Monitor observable get events. */
export const setGetHook = (hook? : GetHook) => getHook = hook;

/** Get the unique identifier for a observable target. */
export const getTargetId = (p : any) => p?.[targetSymbol];

const isSameTarget = (a : any, b : any) => (getTargetId(a) ?? a) === (getTargetId(b) ?? b);

const targetProxy = new Map();
const subscriberTargets = new Map();

/** Subscribe to changes made to an observable. */
export const subscribe = <Target, Value>(
    proxy : Target, 
    prop : string | symbol | null, 
    callback : SubscribeCallback<Target, Value>
) => {
    const targetId = getTargetId(proxy);

    if(!targetId) {
        throw new Error("Object must be observable.")
    }

    const subscriber = {
        prop,
        callback
    }

    if(!subscriberTargets.has(targetId)) {
        subscriberTargets.set(targetId, new Set());
    }

    subscriberTargets.get(targetId).add(subscriber);

    return shouldDispose(() => subscriberTargets.get(targetId)?.delete(subscriber));
}

const onChange = <Target, Value>({ action, target, prop, value, oldValue } : SubscribeEvent<Target, Value>) => {
    if(subscriberTargets.has(getTargetId(target))) {
        const subscribers = [...subscriberTargets.get(getTargetId(target))].filter(s => prop == s.prop || !s.prop);

        if(getDebug() === "verbose") {
            console.log({ count: subscribers.length, action, targetId: getTargetId(target), prop, value, oldValue })
        }

        for(const subscriber of subscribers) {
            queueMicrotask(() => subscriber.callback({ action, prop, value, oldValue }));
        }
    }
}

/** Make a "plain old JS object" into an observable object. */
export const observable = <Target>(target : Target) : Target => {
 
    const createProxy = (target : any) => {

        let targetId = Reflect.get(target, targetSymbol, target);
        if(!targetId) {
            targetId = nextTargetId++;

            Reflect.defineProperty(target, targetSymbol, {
                value: targetId,
                writable: false,
                enumerable: false,
                configurable: false
            });
        }

        const current = new Proxy(target, {
            get: (target, prop, receiver) => {

                if(prop === targetSymbol) {
                    return targetId;
                }
                getHook?.({ action: "get", proxy: current, prop })
            
                const value = target[prop];

                if (typeof value === 'object' && value !== null) {
                    return targetProxy.get(getTargetId(value)) ?? createProxy(value);
                }

                return Reflect.get(target, prop, receiver);
            },
            set: (target, prop, value, receiver) => {
                const oldValue = Reflect.get(target, prop, receiver);

                if(isSameTarget(value, oldValue)) {
                    return true;
                }

                const result = Reflect.set(target, prop, value, receiver);

                onChange({action: "set", target, prop, value, oldValue});

                return result;
            },
            deleteProperty: (target, prop) => {
                const oldValue = Reflect.get(target, prop);
                const result = Reflect.deleteProperty(target, prop);

                onChange({ action: "delete", target, prop, oldValue});

                return result;
            }
        });

        targetProxy.set(getTargetId(target), current);

        return current;
    }

    return createProxy(target);
}